import React, { useEffect, useState, useRef } from 'react';

// 产品数据 API
const PRODUCT_API = 'https://www.lankastudio.cn/api';

// 检测移动端/iOS，减少 DOM 和动画以降低内存压力，避免「网页反复出现问题」
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () => {
      const ua = navigator.userAgent;
      const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
      const isNarrow = window.innerWidth <= 768;
      setIsMobile(isIOS || isNarrow);
    };
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  return isMobile;
}

// 用户偏好减少动画（iOS 辅助功能等）
function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);
    const handler = (e) => setReduced(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return reduced;
}

function ProductShowcase() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const containerRef = useRef(null);
  const isMobile = useIsMobile();
  const prefersReducedMotion = usePrefersReducedMotion();

  // 获取产品列表
  useEffect(() => {
    async function fetchProducts() {
      try {
        const res = await fetch(`${PRODUCT_API}/products/?t=${Date.now()}`);
        if (!res.ok) throw new Error('获取产品失败');
        const data = await res.json();
        
        // 过滤有图片的产品
        const productsWithImages = data.filter(
          p => p.images && Array.isArray(p.images) && p.images.length > 0
        );
        
        setProducts(productsWithImages);
      } catch (error) {
        console.error('获取产品失败:', error);
      } finally {
        setLoading(false);
      }
    }

    fetchProducts();
  }, []);

  if (loading || products.length === 0) {
    return <div style={styles.placeholder}></div>;
  }

  // 限制唯一图数量：背景轮播×多行曾一次打爆 /api/images（HTTP2 PROTOCOL_ERROR）
  const displayProducts = products.slice(0, isMobile ? 8 : 12);
  const duplicatedProducts = [...displayProducts, ...displayProducts];

  const rows = prefersReducedMotion
    ? []
    : isMobile
      ? [{ anim: 'scrollLeftMobile', dur: 60, items: duplicatedProducts }]
      : [
          { anim: 'scrollLeft', dur: 120, items: duplicatedProducts },
          { anim: 'scrollRight', dur: 150, items: [...duplicatedProducts].reverse() },
        ];

  return (
    <div style={styles.container} ref={containerRef}>
      {rows.map((row, ri) => (
        <div key={ri} style={styles.row}>
          <div style={{ ...styles.track, animation: `${row.anim} ${row.dur}s linear infinite` }}>
            {row.items.map((product, index) => (
              <ProductCard key={`row${ri}-${product.id}-${index}`} product={product} isMobile={isMobile} />
            ))}
          </div>
        </div>
      ))}
      <div style={styles.gradientOverlay}></div>
    </div>
  );
}

// 单个产品卡片
function ProductCard({ product, isMobile }) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const imageUrl = product.images && product.images.length > 0
    ? product.images[0].url
    : null;

  if (!imageUrl || imageError) return null;

  const cardStyle = isMobile ? { ...styles.card, width: 120, height: 120 } : styles.card;

  return (
    <div style={cardStyle}>
      <img
        src={imageUrl}
        alt={product.product_name_cn || '产品'}
        style={{ ...styles.image, opacity: imageLoaded ? 1 : 0 }}
        onLoad={() => setImageLoaded(true)}
        onError={() => setImageError(true)}
        loading="lazy"
        decoding="async"
      />
      {!imageLoaded && <div style={styles.imagePlaceholder}></div>}
    </div>
  );
}

const styles = {
  container: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    overflow: 'hidden',
    zIndex: 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: '20px',
    padding: '40px 0',
  },
  placeholder: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(135deg, #1a1d23 0%, #282c34 100%)',
  },
  row: {
    display: 'flex',
    overflow: 'hidden',
    width: '100%',
    WebkitOverflowScrolling: 'touch',
  },
  track: {
    display: 'flex',
    gap: '16px',
    willChange: 'transform',
  },
  card: {
    flexShrink: 0,
    width: '180px',
    height: '180px',
    borderRadius: '16px',
    overflow: 'hidden',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    WebkitBackdropFilter: 'blur(10px)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  },
  image: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transition: 'opacity 0.5s ease',
  },
  imagePlaceholder: {
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
  },
  gradientOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `
      radial-gradient(ellipse at center, transparent 0%, rgba(26, 29, 35, 0.7) 70%, rgba(26, 29, 35, 0.95) 100%),
      linear-gradient(to bottom, rgba(26, 29, 35, 0.8) 0%, transparent 20%, transparent 80%, rgba(26, 29, 35, 0.8) 100%)
    `,
    pointerEvents: 'none',
  },
};

export default ProductShowcase;

